import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import gsap from 'gsap';

const dreamThemes: Record<string, { title: string; symbols: Record<string, string> }> = {
  '飞': {
    title: '飞翔的梦',
    symbols: {
      '自己在飞': '象征渴望自由、突破现状，或现实中感到「被束缚」。人本存在主义视角：你在追寻「 authentic self 」——那个不受社会角色限制的真实自己。',
      '飞不高': '表示有能力但受限，可能是现实中的责任、恐惧或他人的期待在压制你的潜能。',
      '从高处坠落': '积极心理学提醒你：坠落不是失败，而是「重新校准」的过程。这个梦可能暗示你正在经历一个重要的转变期。',
    },
  },
  '水': {
    title: '水的意象',
    symbols: {
      '大海': '荣格心理学中，大海代表集体无意识的深处。梦见大海往往与深层情感的涌动有关——你可能正在面对一些被压抑已久的情绪。',
      '洪水': '叙事疗法视角：洪水象征「旧故事」的瓦解。当旧有的自我认知无法承载新的体验时，内心会产生这种「淹没感」。这往往是成长的开始。',
      '清澈的溪流': '象征情感的流动和净化。你的心境正在变得清澈，内在的冲突正在得到化解。',
      '溺水': '提示你可能在现实中感到「被情绪淹没」。人本存在主义建议你：允许自己「溺水」一会儿——承认脆弱，才能找到真正的浮力。',
    },
  },
  '追': {
    title: '被追逐',
    symbols: {
      '被人追': '被追逐的梦极为常见。叙事疗法认为，追逐者往往代表你「不愿面对的部分自己」。试着在梦中停下来，问问追逐者：「你想告诉我什么？」',
      '追不上': '象征现实中的目标与能力之间的差距感。积极心理学建议你关注「进展」而非「完美」——每一步都是成长。',
      '躲藏': '反映你在现实中回避某些问题或冲突。存在主义提醒你：躲藏只会延长焦虑，面对才能找到出路。',
    },
  },
  '掉': {
    title: '坠落与失去控制',
    symbols: {
      '牙齿掉落': '中国传统解梦认为与「失去力量感」有关。心理学视角：可能反映你对衰老、失去吸引力或表达能力的焦虑。',
      '从楼梯摔下': '象征你在某个「上升通道」中感到不安全感。叙事疗法建议你重新审视「成功」的定义。',
      '大地裂开': '存在主义心理学中，这代表「根基动摇」——你对世界的某些基本信念正在经历重组。这虽然令人不安，但也是深层成长的契机。',
    },
  },
  '考': {
    title: '考试与评估',
    symbols: {
      '考试不会': '即使毕业多年，考试梦仍频繁出现。它象征你在现实中面临某种「被评判」的焦虑。你可能正在担心自己「不够好」。',
      '迟到': '与时间焦虑相关，反映你担心错过某些重要的机会或人生阶段。',
      '找不到考场': '象征方向感的迷失。人本存在主义认为，当我们与内在价值观脱节时，就会产生这种「找不到路」的感觉。',
    },
  },
  '死': {
    title: '死亡与终结',
    symbols: {
      '自己死亡': '梦见自己死亡通常不代表真正的死亡恐惧，而是象征「旧我」的结束和「新我」的诞生。这是深刻的心理转化信号。',
      '亲人去世': '往往反映你对关系的深层焦虑，或象征那个亲人所代表的某种特质正在你的内心「远去」。',
      '葬礼': '仪式性的告别。叙事疗法认为这是「旧故事」的正式终结，为「新故事」的开启腾出空间。',
    },
  },
  '房': {
    title: '房子与空间',
    symbols: {
      '陌生的房子': '房子在梦境心理学中代表「自我」。陌生的房间象征你尚未探索的内在部分——那些潜藏的才能、被忽视的情感。',
      '房子倒塌': '旧有的自我结构正在瓦解。这虽然令人恐惧，但也意味着你有机会重建一个更真实的自我。',
      '找不到房间': '象征你正在寻找内在的某个部分——可能是安全感、归属感，或是一个可以「做真实自己」的空间。',
    },
  },
  '动物': {
    title: '动物象征',
    symbols: {
      '蛇': '荣格心理学中，蛇是「转化」的原型。它象征智慧、本能力量，也可能代表性或潜意识的能量。蛇的出现往往预示重要的内在变化。',
      '猫': '象征直觉、独立和女性特质。猫在梦中的行为反映了你与这些特质的关系。',
      '狗': '代表忠诚、友谊和「内在守护者」。梦中狗的状态反映了你对自己社交关系的感受。',
      '鸟': '象征自由、灵性渴望和「更高的视角」。鸟的种类和行为会提供更具体的含义。',
    },
  },
};

function analyzeDream(dreamText: string): { theme: string; analysis: string; advice: string }[] {
  const results: { theme: string; analysis: string; advice: string }[] = [];
  const lower = dreamText.toLowerCase();

  for (const [keyword, themeData] of Object.entries(dreamThemes)) {
    if (lower.includes(keyword)) {
      let bestMatch = '';
      let bestMatchLength = 0;
      for (const [symbol, meaning] of Object.entries(themeData.symbols)) {
        if (lower.includes(symbol) && symbol.length > bestMatchLength) {
          bestMatch = meaning;
          bestMatchLength = symbol.length;
        }
      }
      const analysis = bestMatch || Object.values(themeData.symbols)[0];

      let advice = '';
      if (keyword === '飞') advice = '尝试在现实中寻找更多「自由表达」的机会，哪怕只是一小步。';
      else if (keyword === '水') advice = '允许自己感受情绪，不要急于「解决」它们。情绪的流动本身就是疗愈。';
      else if (keyword === '追') advice = '试着写下你最近回避的事情，然后问自己：「如果面对它，最坏会怎样？」';
      else if (keyword === '掉') advice = '练习接纳「失控感」——生命中有些事情我们无法掌控，但这不代表失败。';
      else if (keyword === '考') advice = '重新评估你给自己设定的标准。你真的需要「完美」，还是「足够好」就够了？';
      else if (keyword === '死') advice = '思考你生活中正在「结束」的某个阶段，以及你期待「开始」的新篇章。';
      else if (keyword === '房') advice = '花一些时间独处，探索自己的内心世界。冥想或日记都是很好的工具。';
      else if (keyword === '动物') advice = '关注你的本能直觉。有时候身体比头脑更知道答案。';

      results.push({ theme: themeData.title, analysis, advice });
    }
  }

  if (results.length === 0) {
    results.push({
      theme: '个性化解读',
      analysis: `你的梦境「${dreamText.slice(0, 20)}${dreamText.length > 20 ? '...' : ''}」蕴含独特的个人意义。叙事疗法认为，梦是我们内心故事的「夜间剧场」——每个元素都是你生命叙事的隐喻。人本存在主义提醒你：梦的意义不在于「标准答案」，而在于它引发的自我觉察。`,
      advice: '建议你做一个「梦境日记」练习：连续一周记录梦境，然后寻找其中的重复主题。这些「重复」正是你内心最想被倾听的声音。',
    });
  }

  return results;
}

export default function RelaxPage() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [dreamInput, setDreamInput] = useState('');
  const [results, setResults] = useState<ReturnType<typeof analyzeDream>>([]);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!contentRef.current) return;
    gsap.from(contentRef.current.querySelectorAll('.reveal-el'), {
      y: 30, opacity: 0, duration: 0.8, stagger: 0.1, ease: 'power2.out', delay: 0.2,
    });
  }, []);

  const handleAnalyze = () => {
    if (!dreamInput.trim()) return;
    const analysis = analyzeDream(dreamInput.trim());
    setResults(analysis);
    setHasAnalyzed(true);
  };

  return (
    <div style={{ backgroundColor: '#fcfcfc', minHeight: '100vh', fontFamily: '"Noto Sans SC", sans-serif' }}>

      {/* 顶部导航 */}
      <nav className="flex items-center justify-between px-6 lg:px-10 py-4" style={{ borderBottom: '1px solid #e8e0d4' }}>
        <Link to="/" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2c2420', textDecoration: 'none', letterSpacing: '0.05em' }}>
          ← 返回首页
        </Link>
        <p style={{ fontSize: '0.72rem', color: '#a08e78' }}>孔隙心理 · 放松练习</p>
      </nav>

      <div ref={contentRef} className="max-w-3xl mx-auto px-6 py-16">

        {/* 标题 */}
        <div className="text-center mb-12 reveal-el">
          <p className="mb-3" style={{ fontSize: '0.72rem', color: '#a08e78', letterSpacing: '0.12em' }}>
            放松一下 · AI 梦境分析
          </p>
          <h1 className="font-serif" style={{
            fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
            fontWeight: 400,
            lineHeight: 1.2,
            color: '#9080a8',
            textShadow: '1px 1px 0 rgba(255,255,255,0.6), 2px 2px 0 rgba(180,160,200,0.15)',
          }}>
            休息一下 - 探索梦境世界
          </h1>
          <p className="mt-3 mx-auto max-w-lg" style={{ fontSize: '0.85rem', lineHeight: 1.6, color: 'rgba(21, 20, 20, 0.5)' }}>
            中国第一款AI科学梦境心理分析智能体。输入你的梦境内容，
            获取基于心理学理论的趣味解读。梦境分析仅供娱乐和自我探索，不能替代专业心理咨询。
          </p>
        </div>

        {/* 输入区 */}
        <div className="reveal-el mb-10" style={{
          border: '1px solid rgba(180, 150, 200, 0.35)',
          borderRadius: '12px',
          backgroundColor: 'rgba(235, 228, 242, 0.3)',
          padding: '1.5rem',
        }}>
          <label className="block mb-3" style={{ fontSize: '0.78rem', color: 'rgba(21, 20, 20, 0.4)' }}>
            描述你的梦境
          </label>
          <textarea
            value={dreamInput}
            onChange={(e) => setDreamInput(e.target.value)}
            placeholder="比如：我梦见自己在一片漆黑的大海里游泳，远处有一束光..."
            rows={4}
            className="w-full text-sm outline-none resize-none mb-4 px-3 py-2"
            style={{
              border: 'none',
              borderBottom: '1px solid rgba(180, 150, 200, 0.4)',
              backgroundColor: 'rgba(235, 228, 242, 0.25)',
              color: '#151414',
              lineHeight: 1.7,
              borderRadius: '4px',
            }}
          />
          <div className="flex items-center justify-between">
            <p style={{ fontSize: '0.8rem', color: 'rgba(160, 140, 180, 0.55)' }}>
              支持中文描述，越详细解读越有趣
            </p>
            <button
              onClick={handleAnalyze}
              className="px-6 py-2.5 transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: '#fcfcfc', color: '#151414', borderRadius: '9999px', fontSize: '0.8rem', border: '1px solid rgba(180,150,200,0.4)' }}
            >
              🔮 开始分析
            </button>
          </div>
        </div>

        {/* 结果 */}
        {hasAnalyzed && results.length > 0 && (
          <div className="reveal-el space-y-4 mb-10">
            {results.map((result, i) => (
              <div key={i} className="p-6" style={{ border: '1px solid rgba(21, 20, 20, 0.08)', borderRadius: '8px', backgroundColor: 'rgba(21, 20, 20, 0.03)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <span style={{ fontSize: '1.2rem' }}>🌙</span>
                  <h3 style={{ fontSize: '1rem', color: '#151414', fontWeight: 600 }}>{result.theme}</h3>
                </div>
                <p className="mb-4" style={{ fontSize: '0.92rem', lineHeight: 1.7, color: 'rgba(21, 20, 20, 0.7)' }}>{result.analysis}</p>
                <div className="flex items-start gap-2 p-3" style={{ backgroundColor: 'rgba(21, 20, 20, 0.05)', borderRadius: '6px' }}>
                  <span style={{ fontSize: '0.9rem' }}>💡</span>
                  <p style={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'rgba(21, 20, 20, 0.6)' }}>{result.advice}</p>
                </div>
              </div>
            ))}

            <div className="text-center p-4" style={{ border: '1px dashed rgba(21, 20, 20, 0.1)', borderRadius: '8px' }}>
              <p style={{ fontSize: '0.92rem', color: 'rgba(21, 20, 20, 0.35)', lineHeight: 1.6 }}>
                🤝 梦境分析基于荣格分析心理学、叙事疗法和象征心理学理论，仅供娱乐和自我探索。
                <br />
                如果梦境引发了困扰你的情绪，建议联系专业心理咨询师。可到美团搜索「孔隙心理」获取专业服务。
              </p>
            </div>
          </div>
        )}

        {/* 常见主题 */}
        {!hasAnalyzed && (
          <div className="reveal-el">
            <div className="mb-3" style={{ width: '12px', height: '1px', backgroundColor: 'rgba(200, 170, 130, 0.35)' }} />
            <p className="mb-4" style={{ fontSize: '0.78rem', color: 'rgba(21, 20, 20, 0.4)' }}>常见梦境主题</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(dreamThemes).map(([keyword, theme]) => {
                const isSelected = selectedTheme === keyword;
                return (
                  <button
                    key={keyword}
                    onClick={() => { setSelectedTheme(isSelected ? null : keyword); setDreamInput(theme.title); }}
                    className="py-3 px-4 text-left transition-all duration-300"
                    style={{
                      borderRadius: '8px',
                      border: `1px solid ${isSelected ? 'rgba(144, 128, 168, 0.5)' : 'rgba(21, 20, 20, 0.06)'}`,
                      backgroundColor: isSelected ? 'rgba(144, 128, 168, 0.08)' : 'transparent',
                      fontSize: '0.85rem',
                      color: isSelected ? '#9080a8' : 'rgba(21, 20, 20, 0.5)',
                      lineHeight: 1.4,
                      cursor: 'pointer',
                    }}
                  >
                    <span className="block" style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '2px' }}>
                      关键词：{keyword}
                    </span>
                    {theme.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 底部 */}
      <footer className="text-center py-8" style={{ borderTop: '1px solid #e8e0d4' }}>
        <p style={{ fontSize: '0.72rem', color: '#a08e78' }}>
          孔隙心理 · 让心理学融入日常
        </p>
        <Link to="/" style={{ fontSize: '0.78rem', color: '#E8751A', textDecoration: 'none', marginTop: '0.5rem', display: 'inline-block' }}>
          ← 返回首页
        </Link>
      </footer>
    </div>
  );
}
